import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import cmsUtils from '../../utils/cmsUtils';
import { Item } from '../../items/item';
import utils from '../../../utils';
import { createPortal } from "react-dom";
import './style.scss';

const localizer = momentLocalizer(moment);

function Portal({ children }) {
    const [el] = useState(document.createElement('div'));
    useEffect(() => {
        let mount = document.getElementById("portal-root");
        mount.appendChild(el);
        return () => mount.removeChild(el);
    }, [el]);
    return createPortal(children, el);
}

function parseDateFromAustralianFormat(dateStr) {
    const [day, month, year] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day); // Month is 0-based
}

const EventComponent = ({ event }) => {
    return (
        <div style={{
            height: '100%',
            width: '100%',
            overflow: 'hidden'
        }}>
            <div style={{
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                lineHeight: '1.2',
                padding: '2px'
            }}>
                {event.title}
            </div>
        </div>
    );
}

export function KeyDatesCalendar(props) {
    const item = props.item;
    const subItems = item.items || [];
    const cmsOption = props.cmsOption || {};
    const [isOpen, setIsOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    console.log("events",subItems);

    useEffect(() => {
        initNotifications();
        setupEventReminders();
    }, []);

    const initNotifications = async () => {
        await requestNotificationPermission();
        
        if (subItems && subItems.length > 0) {
            prepareNotifications();
            registerPeriodicCheck();
        }
    };

    const requestNotificationPermission = async () => {
        if (!('Notification' in window)) {
            console.log('browser does not support notification feature');
            return false;
        }

        if (Notification.permission === 'granted') return true;

        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('request notification permission failed:', error);
            return false;
        }
    };

    const prepareNotifications = () => {
        console.log("Preparing notifications...");
        // current date
        const today = new Date();
        today.setHours(0, 0, 0, 0); // reset to the start of the day
        
        const notifications = subItems
            .filter(subItem => {
                const eventDate = parseDateFromAustralianFormat(subItem.payloads[1].value);
                // only consider events after today
                return eventDate > today;
            })
            .map(subItem => {
                const eventDate = parseDateFromAustralianFormat(subItem.payloads[1].value);
                // create reminder date (one day before the event)
                const reminderDate = new Date(eventDate);
                reminderDate.setDate(reminderDate.getDate() - 1);
                reminderDate.setHours(12, 0, 0);
                
                // confirm the reminder date is a future date
                if (reminderDate <= new Date()) {
                    console.log(`Skipping reminder for past event: ${subItem.payloads[0].value}`);
                    return null;
                }
                
                return {
                    id: `event-${subItem.id}-${Math.random().toString(36).substring(2, 9)}`,
                    title: subItem.payloads[0].value,
                    description: `${subItem.payloads[1].value} has event: ${subItem.payloads[3].value}`,
                    reminderDate: reminderDate.getTime(),
                    createdAt: Date.now() // record the creation time
                };
            })
            .filter(Boolean);
        
        console.log(`Created ${notifications.length} notifications:`, notifications);
        
        if (notifications.length === 0) {
            console.log("No notifications to schedule");
            return;
        }

        const existingNotifications = JSON.parse(localStorage.getItem('keyDatesNotifications') || '[]');
        const mergedNotifications = [...existingNotifications];

        notifications.forEach(notification => {
            const existingIndex = mergedNotifications.findIndex(n => n.id === notification.id);
            if (existingIndex >= 0) {
                mergedNotifications[existingIndex] = notification;
            } else {
                mergedNotifications.push(notification);
            }
        });

        localStorage.setItem('keyDatesNotifications', JSON.stringify(mergedNotifications));
        syncToServiceWorkerCache(mergedNotifications);

        console.log(`planned ${notifications.length} notifications`);
    };

    const syncToServiceWorkerCache = async (notifications) => {
        if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;

        try {
            const cache = await caches.open('key-dates-cache');
            await cache.put(
                'notifications-data',
                new Response(JSON.stringify(notifications))
            );
        } catch (error) {
            console.error('sync notifications to cache failed:', error);
        }
    };

    const registerPeriodicCheck = async () => {
        if (!('serviceWorker' in navigator)) return;

        try {
            const registration = await navigator.serviceWorker.ready;

            if ('periodicSync' in registration) {
                try {
                    await registration.periodicSync.register('check-key-dates-notifications', {
                        minInterval: 24 * 60 * 60 * 1000
                    });
                    console.log('registered periodic sync');
                    return;
                } catch (error) {
                    console.log('failed to register periodic sync, fallback to visibility check');
                }
            }

            setupVisibilityCheck();
        } catch (error) {
            console.error('register notification check failed:', error);
        }
    };

    const setupVisibilityCheck = () => {
        checkNotifications();

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                checkNotifications();
            }
        });
    };

    const checkNotifications = () => {
        console.log("Checking notifications...");
        const now = Date.now();
        const notifications = JSON.parse(localStorage.getItem('keyDatesNotifications') || '[]');
        const sentNotifications = JSON.parse(localStorage.getItem('sentNotifications') || '[]');
        const updatedNotifications = [];

        notifications.forEach(notification => {
            // check if this notification has been sent
            const alreadySent = sentNotifications.some(sent => sent.id === notification.id);
            
            if (notification.reminderDate <= now) {
                if (!alreadySent) {
                    sendNotification(notification);
                    // record this notification has been sent
                    sentNotifications.push({
                        id: notification.id,
                        sentAt: now
                    });
                } else {
                    console.log(`Notification for "${notification.title}" already sent, skipping.`);
                }
            } else {
                updatedNotifications.push(notification);
            }
        });

        // update sent notifications record, keep the last 7 days
        const recentSentNotifications = sentNotifications.filter(
            sent => (now - sent.sentAt) < 7 * 24 * 60 * 60 * 1000
        );
        localStorage.setItem('sentNotifications', JSON.stringify(recentSentNotifications));
        
        // update waiting to send notifications
        localStorage.setItem('keyDatesNotifications', JSON.stringify(updatedNotifications));
        syncToServiceWorkerCache(updatedNotifications);
    };

    const sendNotification = (notification) => {
        console.log("Sending notification:", notification);
        
        if (Notification.permission !== 'granted') {
            console.log("Permission not granted for notifications");
            return;
        }
        
        try {
            new Notification('Key Dates Reminder', {
                body: notification.description,
                icon: '/favicon.ico',
                tag: notification.id
            });
            console.log("Notification sent successfully");
        } catch (error) {
            console.error('Failed to send notification:', error);
        }
    };

    const sendTestNotification = (notification) => {
        console.log("Sending TEST notification:", notification);
        
        if (Notification.permission !== 'granted') {
            console.log("Permission not granted for notifications");
            return;
        }
        
        try {
            new Notification('TEST: Key Dates Reminder', {
                body: `TEST NOTIFICATION: ${notification.description}`,
                icon: '/favicon.ico',
                tag: `test-${notification.id}`
            });
            console.log("Test notification sent successfully");
        } catch (error) {
            console.error('Failed to send test notification:', error);
        }
    };

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
        setIsOpen(true);
    }

    const cssClass = utils.classNames('cms_item', 'keyDatesCalendar', item.cssClass || item.anchorName || '');

    const formatDate = {
        agendaDateFormat: 'dddd, D MMMM',
    }

    const formatDated = (date) => {
        const formattedDate = new Date(date).toLocaleString('en-Au', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        return formattedDate;
    }

    const myEvents = subItems.map((subItem, index) => {
        return {
            title: subItem.payloads[0].value,
            start: parseDateFromAustralianFormat(subItem.payloads[1].value),
            end: parseDateFromAustralianFormat(subItem.payloads[2].value),
            allDay: true,
        }
    });

    const testNotification = () => {
        console.log("Testing notification...");
        
        if (Notification.permission !== 'granted') {
            console.log("Permission not granted");
            return;
        }
        
        try {
            new Notification('Test Notification', {
                body: 'This is a test notification from KeyDatesCalendar',
                icon: '/favicon.ico',
                tag: 'test-' + Date.now()
            });
            console.log("Test notification sent");
        } catch (error) {
            console.error('Error sending test notification:', error);
        }
    };

    const setupPushNotifications = async () => {
        try {
            // request notification permission
            if (Notification.permission !== 'granted') {
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') return false;
            }
            
            // check service worker support
            if (!('serviceWorker' in navigator)) {
                console.log('Service Worker not supported');
                return false;
            }
            
            // register service worker
            const registration = await navigator.serviceWorker.ready;
            
            // get VAPID public key
            const response = await fetch('/api/PushNotification/vapid-public-key');
            const { publicKey } = await response.json();
            
            // subscribe to push notifications
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey)
            });
            
            // send subscription info to server
            await fetch('/api/PushNotification/subscribe', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(subscription)
            });
            
            return true;
        } catch (error) {
            console.error('Error setting up push notifications:', error);
            return false;
        }
    };

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const setupEventReminders = async () => {
        if (await setupPushNotifications()) {
            // set reminder for future events
            for (const subItem of subItems) {
                const eventDate = parseDateFromAustralianFormat(subItem.payloads[1].value);
                
                // set reminder for future events
                if (eventDate > new Date()) {
                    await fetch('/api/KeyDatesCalendar/reminder', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            title: subItem.payloads[0].value,
                            eventDate: eventDate.toISOString(),
                            eventId: subItem.id,
                            description: subItem.payloads[3].value,
                            url: '/'
                        })
                    });
                }
            }
            console.log('Event reminders set up');
        }
    };

    return (
        <section
            className={cssClass}
            data-cms-item-id={cmsOption.isCmsEdit && item.itemId}
            id={item.anchorName}
        >
            <Calendar
                formats={formatDate}
                views={['month', 'agenda']}
                localizer={localizer}
                events={myEvents}
                startAccessor="start"
                endAccessor="end"
                onSelectEvent={handleSelectEvent}
                style={{ height: '60vh' }}
                components={{
                    event: EventComponent
                }}
            />
            <div className="notification-tools" style={{ marginTop: '20px' }}>
                {/*<button 
                    className="test-notification-btn" 
                    onClick={testNotification}
                    style={{ marginRight: '10px' }}
                >
                    Send Test Notification
                </button>*/}
                
                {/* <button 
                    className="check-notifications-btn" 
                    onClick={checkNotifications}
                >
                    Check Pending Notifications
                </button> */}
            </div>
            <Portal>
                {isOpen && selectedEvent && (
                    <div className="popup-overlay" onClick={() => setIsOpen(false)}>
                        <div className="popup-content" onClick={e => e.stopPropagation()}>
                            <div className="popup-header">
                                <h3 className="popup-title">{selectedEvent.title}</h3>
                                <button
                                    className="popup-close"
                                    onClick={() => setIsOpen(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div className="popup-body">
                                <div className="event-detail">
                                    <span className="detail-label">Start Date:</span>
                                    <span className="detail-value">
                                        {formatDated(selectedEvent.start)}
                                    </span>
                                </div>
                                <div className="event-detail">
                                    <span className="detail-label">End Date:</span>
                                    <span className="detail-value">
                                        {formatDated(selectedEvent.end)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Portal>
        </section>
    );
}